import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTutorial } from './TutorialContext';
import TutorialOverlay from './TutorialOverlay';
import TutorialPopover from './TutorialPopover';

/**
 * Routes where the tutorial must never render or auto-navigate.
 * These are auth-gate pages (login, password change, email confirm, etc.)
 * that may be forced by PrivateRouteWithLayout redirects mid-session.
 */
const AUTH_GATE_ROUTES = [
    '/auth/confirm/email',
    '/password/change',
    '/auth/reset/password',
    '/login',
    '/login-v2',
    '/signup',
    '/signup-v2',
    '/register',
    '/reset-password',
    '/account/recover',
    '/sync_session',
];

const TutorialManager = () => {
    const { 
        isActive, 
        currentStep, 
        currentStepIndex, 
        totalSteps, 
        config, 
        nextStep, 
        prevStep, 
        skipTutorial,
        finishTutorial,
        setInsiderTutorialFlag,
    } = useTutorial();
    
    const history = useHistory();
    const location = useLocation();
    
    const [targetElement, setTargetElement] = useState(null);

    const stepId = currentStep?.id;
    const stepTarget = currentStep?.target;
    const stepRoute = currentStep?.route;
    const autoNavigate = config?.autoNavigate;

    // Guard: if the user lands on any auth-gate route while the tutorial is
    // active (e.g. email confirm, password change), clear the target element
    // so the overlay doesn't render on top of credential forms — but keep
    // isActive=true so the tutorial resumes naturally once they navigate away.
    // (Previously called stopTutorial() here, which caused auto-start to never
    // re-fire after the redirect since authUser/stepsConfig hadn't changed.)
    useEffect(() => {
        if (!isActive) return;
        const isGatedRoute = AUTH_GATE_ROUTES.some(
            (route) => location.pathname === route || location.pathname.startsWith(route + '/')
        );
        if (isGatedRoute) {
            setTargetElement(null);
        }
    }, [location.pathname, isActive]);

    useEffect(() => {
        if (!isActive || !stepId) {
            setTargetElement(null);
            return;
        }

        let isMounted = true;
        let observer = null;
        let interval = null;

        const findElement = () => {
            const el = document.querySelector(stepTarget);
            // Check if element exists and is actually visible in the DOM
            if (el && isMounted && el.getBoundingClientRect().width > 0) {
                setTargetElement(el);

                if (interval) clearInterval(interval);
                if (observer) observer.disconnect();
            } else {
                setTargetElement(null);
            }
        };

        // 1. Check routing logic
        if (stepRoute && location.pathname !== stepRoute) {
            setTargetElement(null); // Clear old target while transitioning
            if (autoNavigate !== false) {
                // Linear mode: Force navigate
                history.push(stepRoute);
                return; // wait for navigation
            } else {
                // Wait mode: We hide the target until user goes to that page
                setTargetElement(null);
                return;
            }
        }

        // 2. Element Hunting
        findElement();

        // If not found immediately, we poll or observe
        const currentEl = document.querySelector(stepTarget);
        if (!currentEl || currentEl.getBoundingClientRect().width === 0) {
            // Use MutationObserver for DOM changes
            observer = new MutationObserver(() => {
                findElement();
            });
            observer.observe(document.body, { childList: true, subtree: true, attributes: true });

            // Fallback interval just in case
            interval = setInterval(() => {
                findElement();
            }, 500);
        }

        return () => {
            isMounted = false;
            if (observer) observer.disconnect();
            if (interval) clearInterval(interval);
        };
    }, [isActive, stepId, stepTarget, stepRoute, location.pathname, history, autoNavigate]);

    if (!isActive || !currentStep || !targetElement) return null;

    return (
        <>
            <TutorialOverlay 
                targetElement={targetElement} 
                config={config} 
            />
            <TutorialPopover 
                targetElement={targetElement} 
                step={currentStep} 
                currentStepIndex={currentStepIndex}
                totalSteps={totalSteps}
                config={config}
                onNext={nextStep}
                onPrev={prevStep}
                onSkip={skipTutorial}
                onFinish={finishTutorial}
                onDoNotShowAgainChange={(doNotShow) => setInsiderTutorialFlag(!doNotShow)}
            />
        </>
    );
};

export default TutorialManager;
